import { c as getApiBaseUrl, l as getAuthToken, o as softParseStandard, s as withGgeGatewayPath } from "./validate.js";
import { f as deleteProofResponseSchema, l as get2ResponseSchema, o as notifyPaidResponseSchema, r as uploadProofResponseSchema } from "./uploadProofSchema.js";
import axios from "axios";
//#region src/__generated__/external/payment/.kubb/serializers.ts
function isFormBody(body) {
	return body instanceof FormData || body instanceof URLSearchParams || body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body) || typeof body === "string";
}
function isDefaultJsonBody(body) {
	return body !== void 0 && body !== null && !isFormBody(body);
}
function appendFormDataValue({ formData, key, value, contentType }) {
	if (value === void 0 || value === null) return;
	if (value instanceof Blob) formData.append(key, value);
	else if (typeof value === "object" && !(value instanceof Date)) {
		const json = JSON.stringify(value);
		formData.append(key, contentType ? new Blob([json], { type: contentType }) : json);
	} else formData.append(key, toValue(value));
}
/**
* Default body serializer: passes binary/form bodies through and JSON-serializes everything else.
* For `multipart/form-data` plain objects become `FormData` and for
* `application/x-www-form-urlencoded` they become `URLSearchParams`. When `encoding` is supplied each
* urlencoded property follows its OpenAPI `style` / `explode` / `allowReserved`.
*
* @example
* ```ts
* defaultBodySerializer({ body: { name: 'odie' } }) // '{"name":"odie"}'
* defaultBodySerializer({ body: { field: 'x' }, contentType: 'multipart/form-data' }) // FormData
* defaultBodySerializer({ body: { tags: ['a', 'b'] }, contentType: 'application/x-www-form-urlencoded', encoding: { tags: { explode: false } } }) // 'tags=a,b'
* defaultBodySerializer({ body: { meta: { a: 1 } }, contentType: 'multipart/form-data', encoding: { meta: { contentType: 'application/json' } } }) // FormData with a typed Blob part
* ```
*/
const defaultBodySerializer = ({ body, contentType, encoding }) => {
	if (body === void 0 || body === null) return void 0;
	if (isFormBody(body)) return body;
	if (contentType?.includes("multipart/form-data")) {
		const formData = new FormData();
		for (const [key, value] of Object.entries(body)) {
			const partContentType = encoding?.[key]?.contentType;
			if (Array.isArray(value)) for (const item of value) appendFormDataValue({
				formData,
				key,
				value: item,
				contentType: partContentType
			});
			else appendFormDataValue({
				formData,
				key,
				value,
				contentType: partContentType
			});
		}
		return formData;
	}
	if (contentType?.includes("application/x-www-form-urlencoded")) {
		if (encoding) return serializeUrlencodedBody(body, encoding);
		return new URLSearchParams(body);
	}
	return JSON.stringify(body);
};
function serializeUrlencodedBody(body, encoding) {
	const parts = [];
	for (const [key, value] of Object.entries(body)) {
		const propertyEncoding = encoding[key];
		parts.push(...propertyEncoding ? serializeStyledQueryParam({
			key,
			value,
			options: propertyEncoding
		}) : serializeDefaultQueryParam(key, value));
	}
	return parts.join("&");
}
function serializeCookie({ name, value, explode }) {
	if (Array.isArray(value)) {
		const items = value.filter(notNullish).map((item) => encodeURIComponent(toValue(item)));
		return explode ? items.map((item) => `${name}=${item}`).join("; ") : `${name}=${items.join(",")}`;
	}
	if (isRecord(value)) {
		const entries = Object.entries(value).filter(([, item]) => notNullish(item));
		if (explode) return entries.map(([key, item]) => `${key}=${encodeURIComponent(toValue(item))}`).join("; ");
		return `${name}=${entries.flatMap(([key, item]) => [key, item]).map((item) => encodeURIComponent(toValue(item))).join(",")}`;
	}
	return `${name}=${encodeURIComponent(toValue(value))}`;
}
/**
* Serializes cookie parameters into a `Cookie` header value using the OpenAPI `form` style, joined
* with `; `. Values are URL-encoded and `explode` is honored per parameter.
*
* @example
* ```ts
* serializeCookies({ session: 'abc', ids: [1, 2] }) // 'session=abc; ids=1,2'
* serializeCookies({ ids: [1, 2] }, { ids: { explode: true } }) // 'ids=1; ids=2'
* ```
*/
function serializeCookies(cookies, styles) {
	const parts = [];
	for (const [name, value] of Object.entries(cookies)) {
		if (value === void 0 || value === null) continue;
		parts.push(serializeCookie({
			name,
			value,
			explode: styles?.[name]?.explode ?? false
		}));
	}
	return parts.join("; ");
}
function appendQueryValue({ search, key, value }) {
	if (value === void 0 || value === null) return;
	if (Array.isArray(value)) {
		for (const item of value) appendQueryValue({
			search,
			key,
			value: item
		});
		return;
	}
	if (isRecord(value)) {
		for (const [prop, propValue] of Object.entries(value)) appendQueryValue({
			search,
			key: `${key}[${prop}]`,
			value: propValue
		});
		return;
	}
	search.append(key, toValue(value));
}
const queryDelimiters = {
	form: ",",
	spaceDelimited: "%20",
	pipeDelimited: "|",
	deepObject: ","
};
function notNullish(value) {
	return value !== void 0 && value !== null;
}
/**
* Renders a primitive parameter value as a string, serializing `Date` to ISO-8601 so dates are
* stable across path, query, cookie, and header locations rather than locale-dependent.
*/
function toValue(value) {
	return value instanceof Date ? value.toISOString() : String(value);
}
/**
* Percent-encodes a value, keeping RFC 3986 reserved characters intact (used when `allowReserved` is set).
*/
function encodeReserved(value) {
	return encodeURI(toValue(value));
}
/**
* Percent-encodes a value, escaping reserved characters (the default query/path encoder).
*/
function encodeComponent(value) {
	return encodeURIComponent(toValue(value));
}
/**
* Whether a value should expand into bracketed/keyed parts. Arrays and `Date` are excluded so they
* are serialized as a unit (a `Date` becomes an ISO string, not its enumerable own properties).
*/
function isRecord(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);
}
/**
* Expands an object or array into `deepObject` query parts, recursing into nested values so
* `{ a: { b: { c: 1 } } }` becomes `a[b][c]=1`. Primitives terminate the recursion.
*/
function serializeDeepObject({ key, value, encode }) {
	if (value === void 0 || value === null) return [];
	if (Array.isArray(value)) return value.flatMap((item, index) => serializeDeepObject({
		key: `${key}[${index}]`,
		value: item,
		encode
	}));
	if (isRecord(value)) return Object.entries(value).filter(([, item]) => notNullish(item)).flatMap(([prop, item]) => serializeDeepObject({
		key: `${key}[${prop}]`,
		value: item,
		encode
	}));
	return [`${encode(key)}=${encode(value)}`];
}
function serializeStyledQueryArray({ key, value, options, encode }) {
	const items = value.filter(notNullish);
	if (options.explode ?? true) return items.map((item) => `${encode(key)}=${encode(item)}`);
	return [`${encode(key)}=${items.map(encode).join(queryDelimiters[options.style ?? "form"])}`];
}
function serializeStyledQueryObject({ key, value, options, encode }) {
	if ((options.style ?? "form") === "deepObject") return serializeDeepObject({
		key,
		value,
		encode
	});
	const entries = Object.entries(value).filter(([, item]) => notNullish(item));
	if (options.explode ?? true) return entries.map(([prop, item]) => `${encode(prop)}=${encode(item)}`);
	return [`${encode(key)}=${entries.flatMap(([prop, item]) => [prop, item]).map(encode).join(",")}`];
}
function serializeStyledQueryParam({ key, value, options }) {
	if (value === void 0 || value === null) return [];
	const encode = options.allowReserved ? encodeReserved : encodeComponent;
	if (Array.isArray(value)) return serializeStyledQueryArray({
		key,
		value,
		options,
		encode
	});
	if (isRecord(value)) return serializeStyledQueryObject({
		key,
		value,
		options,
		encode
	});
	return [`${encode(key)}=${encode(value)}`];
}
function serializeDefaultQueryParam(key, value) {
	const search = new URLSearchParams();
	appendQueryValue({
		search,
		key,
		value
	});
	const result = search.toString();
	return result ? [result] : [];
}
/**
* Default query serializer. Members with `options` metadata follow their OpenAPI `style` / `explode`
* / `allowReserved`. Members without it keep the defaults: arrays explode into repeated keys and
* nested objects use the `deepObject` style (`key[prop]=value`).
*
* @example
* ```ts
* defaultQuerySerializer({ id: [3, 4, 5] }) // 'id=3&id=4&id=5'
* defaultQuerySerializer({ id: [3, 4, 5] }, { id: { style: 'form', explode: false } }) // 'id=3,4,5'
* defaultQuerySerializer({ id: [3, 4, 5] }, { id: { style: 'spaceDelimited', explode: false } }) // 'id=3%204%205'
* defaultQuerySerializer({ id: [3, 4, 5] }, { id: { style: 'pipeDelimited', explode: false } }) // 'id=3|4|5'
* defaultQuerySerializer({ a: { b: 1 } }, { a: { style: 'deepObject' } }) // 'a%5Bb%5D=1'
* defaultQuerySerializer({ a: { b: { c: 1 } } }, { a: { style: 'deepObject' } }) // 'a%5Bb%5D%5Bc%5D=1'
* ```
*/
const defaultQuerySerializer = (params, options) => {
	const parts = [];
	for (const [key, value] of Object.entries(params)) {
		const paramOptions = options?.[key];
		parts.push(...paramOptions ? serializeStyledQueryParam({
			key,
			value,
			options: paramOptions
		}) : serializeDefaultQueryParam(key, value));
	}
	return parts.join("&");
};
function serializePathPrimitive({ name, value, style }) {
	const encoded = encodeComponent(value);
	if (style === "label") return `.${encoded}`;
	if (style === "matrix") return `;${name}=${encoded}`;
	return encoded;
}
function serializePathArray({ name, value, style, explode }) {
	const items = value.map(encodeComponent);
	if (style === "label") return `.${items.join(explode ? "." : ",")}`;
	if (style === "matrix") return explode ? items.map((item) => `;${name}=${item}`).join("") : `;${name}=${items.join(",")}`;
	return items.join(",");
}
function serializePathObject({ name, value, style, explode }) {
	const members = Object.entries(value).map(([key, item]) => explode ? `${encodeComponent(key)}=${encodeComponent(item)}` : `${encodeComponent(key)},${encodeComponent(item)}`);
	if (style === "label") return `.${members.join(explode ? "." : ",")}`;
	if (style === "matrix") return explode ? members.map((member) => `;${member}`).join("") : `;${name}=${members.join(",")}`;
	return members.join(",");
}
/**
* Default path serializer honoring the OpenAPI `style` / `explode` metadata. Without metadata it
* falls back to `simple` style with `explode: false`. Replaces the previous `String(value)`
* interpolation, which emitted `[object Object]` for object path params.
*
* @example
* ```ts
* defaultPathSerializer({ name: 'id', value: [3, 4, 5] }) // '3,4,5'
* defaultPathSerializer({ name: 'id', value: [3, 4, 5], options: { style: 'label', explode: true } }) // '.3.4.5'
* defaultPathSerializer({ name: 'id', value: [3, 4, 5], options: { style: 'matrix', explode: true } }) // ';id=3;id=4;id=5'
* defaultPathSerializer({ name: 'pt', value: { x: 1, y: 2 } }) // 'x,1,y,2'
* ```
*/
const defaultPathSerializer = ({ name, value, options }) => {
	if (value === void 0 || value === null) return "";
	const style = options?.style ?? "simple";
	const explode = options?.explode ?? false;
	if (Array.isArray(value)) return serializePathArray({
		name,
		value,
		style,
		explode
	});
	if (isRecord(value)) return serializePathObject({
		name,
		value,
		style,
		explode
	});
	return serializePathPrimitive({
		name,
		value,
		style
	});
};
function serializeHeaderValue(value, explode) {
	if (Array.isArray(value)) return value.filter(notNullish).map(toValue).join(",");
	if (!isRecord(value)) return toValue(value);
	const entries = Object.entries(value).filter(([, item]) => notNullish(item));
	if (explode) return entries.map(([key, item]) => `${key}=${toValue(item)}`).join(",");
	return entries.flatMap(([key, item]) => [key, item]).map(toValue).join(",");
}
/**
* Serializes array and object header parameters with the OpenAPI `simple` style before they are
* merged. Header values are not URL-encoded. Primitive values and headers without metadata pass
* through untouched.
*/
function applyHeaderStyles(headers, styles) {
	if (!headers || !styles) return headers;
	return (Array.isArray(headers) ? headers : Object.entries(headers)).map(([key, value]) => {
		const style = styles[key];
		if (!style || value === void 0 || value === null || typeof value !== "object") return [key, value];
		return [key, serializeHeaderValue(value, style.explode ?? false)];
	});
}
//#endregion
//#region src/__generated__/external/payment/.kubb/standardSchema.ts
/**
* Thrown by `validateStandardSchema` when validation fails. Carries the raw `issues` array from
* the schema's `validate` result so callers receive a uniform error shape regardless of which
* schema library is in use.
*/
var ParseError = class extends Error {
	issues;
	constructor({ issues, message }) {
		super(message ?? "Validation failed");
		this.name = "ParseError";
		this.issues = issues;
	}
};
/**
* Validates `value` against a Standard Schema-compatible `schema`. Returns the parsed output on
* success; throws `ParseError` with the schema's `issues` on failure. Handles both sync and async
* `validate` implementations transparently.
*
* @example
* const pet = await validateStandardSchema(PetSchema, rawData)
*/
async function validateStandardSchema(schema, value) {
	const result = await schema["~standard"].validate(value);
	if (result.issues) throw new ParseError({ issues: result.issues });
	return result.value;
}
//#endregion
//#region src/__generated__/external/payment/.kubb/client.ts
/**
* Thrown for a non-2xx response, so a resolved call always means success.
*/
var ResponseError = class extends Error {
	data;
	status;
	statusText;
	contentType;
	request;
	response;
	constructor(config) {
		super(`Request failed with status ${config.status}${config.statusText ? ` ${config.statusText}` : ""}`);
		this.name = "ResponseError";
		this.data = config.data;
		this.status = config.status;
		this.statusText = config.statusText;
		this.contentType = config.contentType;
		this.request = config.request;
		this.response = config.response;
	}
};
function serializeHeaders(headers) {
	if (!headers) return {};
	const entries = Array.isArray(headers) ? headers : Object.entries(headers);
	const result = {};
	for (const [key, value] of entries) {
		if (value === void 0 || value === null) continue;
		result[key] = typeof value === "string" ? value : typeof value === "object" ? JSON.stringify(value) : String(value);
	}
	return result;
}
function mergeHeaders(...sources) {
	return Object.assign({}, ...sources.map(serializeHeaders));
}
function getHeader(headers, name) {
	const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
	return key ? headers[key] : void 0;
}
function hasHeader(headers, name) {
	return Object.keys(headers).some((k) => k.toLowerCase() === name.toLowerCase());
}
/**
* Joins the URL parts, interpolates URL-encoded `{param}` segments, and appends the serialized query, backing `getUrl`.
*/
function serializeUrl({ parts, pathParams, search, pathSerializer = defaultPathSerializer, pathStyles }) {
	return parts.filter(Boolean).join("").replace(/\{([^{}]+)\}/g, (_, key) => pathSerializer({
		name: key,
		value: pathParams[key],
		options: pathStyles?.[key]
	})) + (search ? `?${search}` : "");
}
/**
* Wraps an axios interceptor registration behind the shared `use` / `eject` / `update` API, mapping a stable external id onto axios's own so `update` can swap a handler in place.
*/
function createInterceptorChannel(register, ejectNative) {
	const ids = /* @__PURE__ */ new Map();
	let counter = 0;
	return {
		use(fn) {
			const id = ++counter;
			ids.set(id, register(fn));
			return id;
		},
		eject(id) {
			const nativeId = ids.get(id);
			if (nativeId === void 0) return;
			ejectNative(nativeId);
			ids.delete(id);
		},
		update(id, fn) {
			const nativeId = ids.get(id);
			if (nativeId !== void 0) ejectNative(nativeId);
			ids.set(id, register(fn));
		}
	};
}
/**
* Walks the per-operation security in order and places the first resolved token on the request, mutating `headers` / `query` in place.
*/
async function resolveAuth(params) {
	const { security, auth, headers, query } = params;
	if (!security?.length || auth === void 0) return;
	for (const scheme of security) {
		const token = typeof auth === "function" ? await auth(scheme) : auth;
		if (token === void 0) continue;
		if (scheme.type === "apiKey") {
			const name = scheme.name ?? "Authorization";
			if (scheme.in === "query") {
				if (query[name] === void 0) query[name] = token;
			} else if (scheme.in === "cookie") headers.Cookie = [headers.Cookie, `${name}=${token}`].filter(Boolean).join("; ");
			else if (!hasHeader(headers, name)) headers[name] = token;
		} else if (!hasHeader(headers, "Authorization")) headers.Authorization = scheme.scheme === "basic" ? `Basic ${btoa(token)}` : `Bearer ${token}`;
		return;
	}
}
async function runValidator(validator, value) {
	if (!validator) return value;
	return validateStandardSchema(validator, value);
}
/**
* The base media type of a `Content-Type` value, lowercased and stripped of any `; charset=...` parameters.
*/
function baseContentType(value) {
	if (!value) return void 0;
	return value.split(";")[0].trim().toLowerCase() || void 0;
}
/**
* Reads the negotiated response content type from the response headers as a base media type.
*/
function getResponseContentType(headers) {
	if (!headers) return void 0;
	const value = headers["content-type"] ?? headers["Content-Type"];
	return baseContentType(typeof value === "string" ? value : void 0);
}
/**
* Normalizes the `contentType` option to its `{ request, response }` form, treating a bare string as the request content type.
*/
function resolveContentType(contentType) {
	if (typeof contentType === "string") return { request: contentType };
	return contentType ?? {};
}
/**
* The per-concern serializers for a call, the per-call serializer winning over the client's and
* falling back to the defaults.
*/
function resolveSerializers({ config, requestConfig }) {
	return {
		querySerializer: requestConfig.serializer?.query ?? config.serializer?.query ?? defaultQuerySerializer,
		bodySerializer: requestConfig.serializer?.body ?? config.serializer?.body ?? defaultBodySerializer,
		pathSerializer: requestConfig.serializer?.path ?? config.serializer?.path ?? defaultPathSerializer
	};
}
/**
* Resolves everything a call needs before it touches axios: merged headers with the negotiated
* content type, auth on headers or query, serialized cookies, the validated and serialized body,
* and the final axios request config with `throwOnError` riding `validateStatus`.
*/
async function resolveRequest({ config, requestConfig }) {
	const { querySerializer, bodySerializer, pathSerializer } = resolveSerializers({
		config,
		requestConfig
	});
	const codecs = {
		...config.codecs,
		...requestConfig.codecs
	};
	const headers = mergeHeaders(config.headers, applyHeaderStyles(requestConfig.headers, requestConfig.styles?.header));
	const { request: requestContentTypeOption, response: responseContentType } = resolveContentType(requestConfig.contentType);
	const requestContentType = requestContentTypeOption ?? getHeader(headers, "content-type");
	if (responseContentType && !hasHeader(headers, "accept")) headers["Accept"] = responseContentType;
	const query = { ...requestConfig.query ?? requestConfig.params };
	await resolveAuth({
		security: requestConfig.security,
		auth: requestConfig.auth ?? config.auth,
		headers,
		query
	});
	const authToken = getAuthToken();
	if (authToken && !hasHeader(headers, "authorization")) headers["Authorization"] = `Bearer ${authToken}`;
	if (requestConfig.cookies) {
		const cookie = serializeCookies(requestConfig.cookies, requestConfig.styles?.cookie);
		if (cookie) headers.Cookie = [headers.Cookie, cookie].filter(Boolean).join("; ");
	}
	const validatedBody = await runValidator(requestConfig.validator?.request, requestConfig.body);
	const requestContentTypeBase = baseContentType(requestContentType);
	const contentCodec = requestContentTypeBase ? codecs[requestContentTypeBase] : void 0;
	const usesDefaultBodySerializer = !contentCodec?.serialize && bodySerializer === defaultBodySerializer;
	const body = contentCodec?.serialize ? contentCodec.serialize(validatedBody, requestContentType) : bodySerializer({
		body: validatedBody,
		contentType: requestContentType,
		encoding: requestConfig.styles?.body
	});
	if (body instanceof FormData) {
		for (const key of Object.keys(headers)) if (key.toLowerCase() === "content-type") delete headers[key];
	} else if (requestContentTypeOption) headers["Content-Type"] = requestContentTypeOption;
	else if (usesDefaultBodySerializer && isDefaultJsonBody(validatedBody) && !hasHeader(headers, "content-type")) headers["Content-Type"] = "application/json";
	const pathParams = requestConfig.path ?? {};
	const url = withGgeGatewayPath((requestConfig.url ?? "").replace(/\{([^{}]+)\}/g, (_, key) => pathSerializer({
		name: key,
		value: pathParams[key],
		options: requestConfig.styles?.path?.[key]
	})));
	const throwOnError = requestConfig.throwOnError ?? config.throwOnError ?? true;
	const validateStatus = requestConfig.validateStatus ?? config.validateStatus ?? (throwOnError ? (status) => status >= 200 && status < 300 : () => true);
	const axiosConfig = {
		...config.options || requestConfig.options ? {
			...config.options,
			...requestConfig.options
		} : void 0,
		url,
		baseURL: requestConfig.baseURL ?? config.baseURL ?? getApiBaseUrl(),
		method: requestConfig.method ?? "GET",
		headers,
		params: query,
		paramsSerializer: (params) => querySerializer(params, requestConfig.styles?.query),
		data: body,
		transformRequest: (data) => data,
		signal: requestConfig.signal,
		responseType: requestConfig.responseType,
		validateStatus
	};
	if (requestConfig.responseType === "stream" && !axiosConfig.adapter) axiosConfig.adapter = "fetch";
	return {
		axiosConfig,
		codecs,
		throwOnError
	};
}
/**
* Turns an axios response into the call result: decodes the body through the matching codec and
* validates the success or error body. A thrown axios error never reaches this, so `error` here is
* always the body of a non-2xx response that `validateStatus` let through.
*/
async function settleResponse({ response, codecs, validator }) {
	const isSuccess = response.status >= 200 && response.status < 300;
	const contentType = getResponseContentType(response.headers);
	let decoded = response.data;
	if (contentType) {
		const codec = codecs[contentType];
		if (codec?.deserialize) decoded = await codec.deserialize(response.data, contentType);
	}
	const data = isSuccess ? await softParseStandard(validator?.response, decoded, `${response.config?.method ?? "?"} ${response.config?.url ?? "?"}`) : void 0;
	const error = isSuccess ? void 0 : await runValidator(validator?.error, decoded);
	return {
		status: response.status,
		data,
		error,
		contentType,
		request: response.config,
		response
	};
}
/**
* Builds the shared client core bound to an axios instance (defaulting to `axios.create()`), with `throwOnError` riding axios's `validateStatus`.
*/
function createClientCore(options = {}) {
	let config = { ...options };
	const instance = config.transport ?? axios.create();
	const requestManager = instance.interceptors.request;
	const responseManager = instance.interceptors.response;
	const interceptors = {
		request: createInterceptorChannel((fn) => requestManager.use(fn), (id) => requestManager.eject(id)),
		response: createInterceptorChannel((fn) => responseManager.use(fn), (id) => responseManager.eject(id)),
		error: createInterceptorChannel((fn) => responseManager.use(void 0, async (error) => {
			await fn(error);
			return Promise.reject(error);
		}), (id) => responseManager.eject(id))
	};
	const client = (async (requestConfig) => {
		const activeInstance = requestConfig.transport ?? config.transport ?? instance;
		const { axiosConfig, codecs, throwOnError } = await resolveRequest({
			config,
			requestConfig
		});
		try {
			return await settleResponse({
				response: await activeInstance.request(axiosConfig),
				codecs,
				validator: requestConfig.validator
			});
		} catch (error) {
			const axiosError = error;
			if (throwOnError && axiosError.response) throw new ResponseError({
				data: axiosError.response.data,
				status: axiosError.response.status,
				statusText: axiosError.response.statusText,
				contentType: getResponseContentType(axiosError.response.headers),
				request: axiosError.config,
				response: axiosError.response
			});
			throw error;
		}
	});
	client.getConfig = () => config;
	client.setConfig = (next) => {
		config = {
			...config,
			...next,
			headers: {
				...serializeHeaders(config.headers),
				...serializeHeaders(next.headers)
			}
		};
		return config;
	};
	client.getUrl = (requestConfig) => {
		const { querySerializer, pathSerializer } = resolveSerializers({
			config,
			requestConfig
		});
		const query = { ...requestConfig.query ?? requestConfig.params };
		return serializeUrl({
			parts: [requestConfig.baseURL ?? config.baseURL, requestConfig.url],
			pathParams: requestConfig.path ?? {},
			search: querySerializer(query, requestConfig.styles?.query),
			pathSerializer,
			pathStyles: requestConfig.styles?.path
		});
	};
	client.interceptors = interceptors;
	client.createClient = (next) => createClientCore({
		...config,
		...next
	});
	return client;
}
async function* readBytes(stream) {
	if (!("getReader" in stream)) {
		yield* stream;
		return;
	}
	const reader = stream.getReader();
	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) return;
			yield value;
		}
	} finally {
		await reader.cancel().catch(() => {});
	}
}
function parseEvent(raw) {
	const data = [];
	const event = { data: void 0 };
	let seen = false;
	for (const line of raw.split("\n")) {
		if (!line || line.startsWith(":")) continue;
		seen = true;
		const index = line.indexOf(":");
		const field = index === -1 ? line : line.slice(0, index);
		const value = index === -1 ? "" : line.slice(index + 1).replace(/^ /, "");
		if (field === "data") data.push(value);
		else if (field === "event") event.event = value;
		else if (field === "id") event.id = value;
		else if (field === "retry" && Number.isFinite(Number(value))) event.retry = Number(value);
	}
	if (!seen) return void 0;
	if (data.length) {
		const joined = data.join("\n");
		try {
			event.data = JSON.parse(joined);
		} catch {
			event.data = joined;
		}
	}
	return event;
}
/**
* Parses a `text/event-stream` body into typed Server-Sent Events, consumed with `for await` and stopped early by breaking the loop.
*/
async function* parseEventStream(stream) {
	const decoder = new TextDecoder();
	const normalize = (text) => text.replace(/\r\n|\r/g, "\n");
	let buffer = "";
	for await (const chunk of readBytes(stream)) {
		const blocks = normalize(buffer + decoder.decode(chunk, { stream: true })).split("\n\n");
		buffer = blocks.pop() ?? "";
		for (const block of blocks) {
			const event = parseEvent(block);
			if (event) yield event;
		}
	}
	const event = parseEvent(normalize(buffer + decoder.decode()));
	if (event) yield event;
}
/**
* Wraps a transport result whose `data` is a streaming body into an `EventStreamResult`, exposing
* the parsed events as a typed async iterator. Generated SSE operations call this.
*/
async function toEventStream(result) {
	const { data, response } = await result;
	return {
		response,
		stream: parseEventStream(data)
	};
}
const client = createClientCore();
const createClient = (config) => client.createClient(config);
//#endregion
//#region src/__generated__/external/payment/client/deleteProof.ts
/**
* @summary Удалить подтверждение оплаты (владелец, до уведомления)
* {@link /api/mdm/external/api/external/requests/:id/payment/proof}
*/
function deleteProof(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "DELETE",
		url: "/api/mdm/external/api/external/requests/{id}/payment/proof",
		validator: { response: deleteProofResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/external/payment/client/get2.ts
/**
* @summary Данные оплаты по заявке (для заявителя)
* {@link /api/mdm/external/api/external/requests/:id/payment}
*/
function get2(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/external/api/external/requests/{id}/payment",
		validator: { response: get2ResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/external/payment/client/notifyPaid.ts
/**
* @summary Уведомить об оплате (WAITING_PAYMENT → IN_REVIEW)
* {@link /api/mdm/external/api/external/requests/:id/payment/notify}
*/
function notifyPaid(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "POST",
		url: "/api/mdm/external/api/external/requests/{id}/payment/notify",
		validator: { response: notifyPaidResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/external/payment/client/uploadProof.ts
/**
* @summary Загрузить/заменить подтверждение оплаты (владелец, до уведомления)
* {@link /api/mdm/external/api/external/requests/:id/payment/proof}
*/
function uploadProof(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "POST",
		url: "/api/mdm/external/api/external/requests/{id}/payment/proof",
		validator: { response: uploadProofResponseSchema },
		contentType: { request: "multipart/form-data" },
		...config
	});
}
//#endregion
export { defaultQuerySerializer as _, ResponseError as a, createClientCore as c, toEventStream as d, ParseError as f, defaultPathSerializer as g, defaultBodySerializer as h, deleteProof as i, parseEventStream as l, applyHeaderStyles as m, notifyPaid as n, client as o, validateStandardSchema as p, get2 as r, createClient as s, uploadProof as t, resolveAuth as u, isDefaultJsonBody as v, serializeCookies as y };

//# sourceMappingURL=uploadProof.js.map